import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api/client';
import { API_CARD_DEFS_URL, API_ELEMENT_SETS_URL } from '../utils';
import { createElementResolver, createNotesResolver, createPrimaryDateResolver, indexCardsById, indexElementSetsById } from '../utils/cards';
import { normalizeId } from '../utils/ids';
import { readSavedCopy, writeSavedCopy } from '../utils/savedCopy';
import { getSessionToken, getSessionUserId, useOnSessionChange } from '../utils/session';

// A miss is reported by whatever renders an item; the refetch it earns is
// debounced so a page of forty items of one new card asks once, spaced so a
// card that truly does not exist (deleted, or never readable) cannot make a
// grid poll, and remembered for a while for the same reason.
const MISS_DEBOUNCE_MS = 400;
const MISS_MIN_INTERVAL_MS = 5000;
const MISS_REMEMBER_MS = 60000;
// At most this many cards are fetched ONE BY ONE per miss burst (a global
// collection's cards never come in the list — see fetchSingly below).
const MISS_SINGLE_MAX = 20;
const NO_IDS = Object.freeze(new Set());

const mergeById = (base, extra) => {
	if (!extra.length) return base;
	const seen = new Set(base.map(doc => normalizeId(doc?._id)));
	return [...base, ...extra.filter(doc => !seen.has(normalizeId(doc?._id)))];
};

/**
 * App-wide cache of card definitions. Definitions are fetched on the first
 * mount of a consumer (via useCardDefinitions) and shared everywhere: the
 * items page, the item grid, the Add item picker, the cards page.
 *
 * Nothing is fetched without a session. The command palette in Layout mounts
 * on every route — the sign-in form, Home signed out, the docs, the 404 page —
 * and `GET /card-definitions` is an authenticated read: asked with no token it
 * is a 401, and the api client's 401 redirect bounced signed-out readers to
 * /login. Signed out, the cache settles EMPTY (loading false, loaded false)
 * and asks nothing; the sign-in (utils/session.js) is what loads it.
 *
 * The cache is keyed on the session token, and only a SUCCESSFUL fetch
 * latches it:
 * - a failed fetch (a deploy restart, a 429) is retried by the next consumer
 *   mount instead of leaving the whole session with "Card definitions failed
 *   to load" and an empty card picker;
 * - a token change (login, logout, another account signing in on the same
 *   tab) drops the previous account's cards and refetches, so one user's
 *   definitions never render for the next.
 *
 * A SAVED COPY of the last successful load (utils/savedCopy.js, KPR-1080)
 * seeds the cache on a full page load, so cards render before the fetch lands.
 * It is a speed-up: the fetch always runs and replaces it, `loaded` stays false
 * until it succeeds (so a card the copy lacks is not a verdict), and a failed
 * fetch keeps showing the copy. The copy goes with the session.
 *
 * Element sets (docs/plans/element-sets-and-me.md) load BESIDE the cards,
 * with the same latch: `GET /api/element-sets` is every set the caller may
 * read (global + their visible collections'), and the resolvers below fold
 * each card's attached sets into its effective element list exactly as the
 * API does. A sets fetch that fails leaves the cards usable — an older API
 * without the endpoint answers 404 — and is retried with the next load.
 *
 * Exposes:
 * - cards / cardsById: the raw definitions
 * - elementSets / elementSetsById: the readable element sets
 * - getElementsForCard(id): element defs for a card including inherited
 *   parent-chain elements and every attached element set's (memoized)
 * - cardNotesEnabled(id): whether the card's Notes field is on, resolved
 *   through the same parent chain (the most specific explicit setting wins;
 *   absent anywhere = on)
 * - cardPrimaryDate(id): the card's primary-date spec ({ kind, field, label,
 *   dataType, element } — utils/cards.js), resolved through the same chain
 *   (absent anywhere = created)
 * - loading: true until the first fetch settles
 * - loaded: a fetch has SUCCEEDED for the current session — only then is
 *   "the cache does not hold card X" a verdict rather than a fetch that
 *   failed and is about to be retried (see load below)
 * - error: the fetch error, if any
 * - refresh(): force a refetch (e.g. after creating/editing a card)
 * - refreshElementSets(): the same, after creating/editing an element set
 *   (one fetch reloads both lists; the two names say which record changed)
 * - noteMissingCards(ids): "an item on screen wears a card this cache does
 *   not hold" — a card added since this session loaded, by another person or
 *   an API client. The cache refetches itself (debounced, rate-limited)
 *   rather than leaving the items to render as bare titles until someone
 *   reloads the page. Ids a refetch still does not produce are remembered
 *   for a minute so a deleted card's orphans cannot make a list poll. A
 *   no-op until the cache has loaded once: before that, a miss is not a
 *   verdict. hooks/useEnsureCards.js is the one-line way to call it.
 * - isCardMissed(id): has that refetch looked for the card and come back
 *   without it? The verdict a surface waiting on one card (the item form)
 *   reads before it calls the card missing.
 * - version: bumps on every successful load, for caches that hang off the
 *   card list (a collection's tile layouts) and should refetch beside it
 */
const CardDefinitionsContext = createContext(null);

// The bearer token the api client sends, read the same way it reads it
// (utils/session.js). Null when signed out.
function readToken() {
	return getSessionToken();
}

// The saved copy this account's last load left (utils/savedCopy.js, KPR-1080):
// what the cache starts from on a full page load, so item cards render with
// their definitions before the fetch lands. Never a verdict — `loaded` stays
// false until a fetch SUCCEEDS, and that fetch replaces it.
const SAVED_NAME = 'card-definitions';
const readSaved = () => {
	const copy = readSavedCopy(SAVED_NAME);
	return copy && Array.isArray(copy.cards) && Array.isArray(copy.sets) ? copy : null;
};

export function CardDefinitionsProvider({ children }) {
	const [saved] = useState(readSaved);
	const [listedCards, setCards] = useState(() => saved?.cards || []);
	const [listedSets, setElementSets] = useState(() => saved?.sets || []);
	// Cards and sets fetched ONE BY ONE because the list endpoint does not
	// carry them — a GLOBAL collection's cards (docs/plans/personal-discover-
	// global.md §4.3: the list stays free of every published collection's
	// cards, a single card is readable by anyone who reads its collection).
	// Merged under the listed ones; a forced reload keeps them.
	const [extraCards, setExtraCards] = useState([]);
	const [extraSets, setExtraSets] = useState([]);
	const cards = useMemo(() => mergeById(listedCards, extraCards), [listedCards, extraCards]);
	const elementSets = useMemo(() => mergeById(listedSets, extraSets), [listedSets, extraSets]);
	const [settled, setSettled] = useState(false);
	const [fetching, setFetching] = useState(false);
	const [error, setError] = useState(null);
	const [loaded, setLoaded] = useState(false);
	const [version, setVersion] = useState(0);
	// { token } once a fetch SUCCEEDED for that token; null until then.
	const loadedForRef = useRef(null);
	const inFlightTokenRef = useRef(undefined); // token of the request in flight
	// The on-demand refetch (noteMissingCards below).
	const missQueueRef = useRef(new Set());       // ids waiting for the next refetch
	const missRememberedRef = useRef(new Map());  // id -> when a refetch still lacked it
	const missTimerRef = useRef(null);
	const missLastAtRef = useRef(0);
	// The VERDICTS of the on-demand refetch: ids it went looking for and did
	// not get (no such card, not readable, or the fetch failed). State, not a
	// ref, so a surface waiting on one card (the item form) hears the answer.
	const [missedCardIds, setMissedCardIds] = useState(NO_IDS);
	const cardsByIdRef = useRef({});

	const load = useCallback(async ({ force = false } = {}) => {
		if (!getSessionUserId()) {
			// Signed out: settled, empty, and nothing asked. (Whatever the last
			// account held was dropped by the session reset below.)
			setSettled(true);
			return undefined;
		}
		const token = readToken();
		const loaded = loadedForRef.current;
		if (!force) {
			if (loaded && loaded.token === token) return; // fresh cache
			if (inFlightTokenRef.current === token) return; // already fetching for this session
		}
		if (loaded && loaded.token !== token) {
			// Another session: the previous account's cards must not linger,
			// even if the fetch for this one fails.
			loadedForRef.current = null;
			setCards([]);
			setElementSets([]);
			setExtraCards([]);
			setExtraSets([]);
			setLoaded(false);
		}
		inFlightTokenRef.current = token;
		setFetching(true);
		setError(null);
		try {
			// Both requests leave together; the cards' answer is the one that
			// latches the cache. The sets' is a nicety on top: a refusal (or an
			// API without the endpoint) reads as no sets, never as an error.
			const cardsRequest = api.get(API_CARD_DEFS_URL);
			const setsRequest = new Promise(resolve => resolve(api.get(API_ELEMENT_SETS_URL)))
				.then(response => (Array.isArray(response?.data) ? response.data : []))
				.catch(() => []);
			const { data } = await cardsRequest;
			const sets = await setsRequest;
			if (readToken() !== token) return; // session changed mid-flight; its own fetch wins
			setCards(data || []);
			setElementSets(sets);
			writeSavedCopy(SAVED_NAME, { cards: data || [], sets });
			loadedForRef.current = { token };
			setLoaded(true);
			setVersion(v => v + 1);
			return data || [];
		} catch (err) {
			if (readToken() !== token) return;
			setError(err);
		} finally {
			if (inFlightTokenRef.current === token) {
				inFlightTokenRef.current = undefined;
				setFetching(false);
			}
			setSettled(true);
		}
	}, []);

	// Another account (or none) on this tab. load() already drops the last
	// account's cards when it next runs under a new token, but it runs only
	// when a consumer mounts — the shell's consumers stay mounted through a
	// sign-in, so the drop happens here, at once, and the next account's
	// cards load here too. Signed out, load() only settles the cache empty.
	useOnSessionChange(() => {
		loadedForRef.current = null;
		inFlightTokenRef.current = undefined;
		missQueueRef.current = new Set();
		missRememberedRef.current = new Map();
		setMissedCardIds(NO_IDS);
		setCards([]);
		setElementSets([]);
		setExtraCards([]);
		setExtraSets([]);
		setLoaded(false);
		setError(null);
		load();
	});

	const refresh = useCallback(() => {
		// A deliberate refresh (a card was just created or edited here) starts
		// the miss memory over: what was missing may exist now.
		missRememberedRef.current = new Map();
		setMissedCardIds(NO_IDS);
		return load({ force: true });
	}, [load]);

	const cardsById = useMemo(() => indexCardsById(cards), [cards]);
	cardsByIdRef.current = cardsById;
	const setsByIdRef = useRef({});

	/**
	 * Merge cards (and element sets) fetched elsewhere — the global lookup
	 * targets, a global collection's own cards — into the cache.
	 */
	const addCards = useCallback((more = [], moreSets = []) => {
		if (more.length) setExtraCards(prev => mergeById(prev, more));
		if (moreSets.length) setExtraSets(prev => mergeById(prev, moreSets));
	}, []);

	/**
	 * The ids a refetch of the LIST still lacks, one GET each (capped): a card
	 * of a global collection is readable singly though the list leaves it out.
	 * Its parents come too, and its collection's element sets when it names
	 * one the cache lacks. Returns the ids that could not be read at all.
	 */
	const fetchSingly = useCallback(async (ids, have) => {
		const got = [];
		const sets = [];
		const setCollections = new Set();
		const queue = ids.slice(0, MISS_SINGLE_MAX);
		const unreadable = [];
		const seen = new Set();
		while (queue.length) {
			const id = queue.shift();
			if (!id || seen.has(id) || have[id]) continue;
			seen.add(id);
			try {
				const { data } = await api.get(`${API_CARD_DEFS_URL}/${id}`);
				if (!data || !data._id) { unreadable.push(id); continue; }
				got.push(data);
				const parent = normalizeId(data.parentCardId);
				if (parent && !have[parent] && !seen.has(parent) && seen.size + queue.length < MISS_SINGLE_MAX * 2) queue.push(parent);
				const cid = data.scope === 'collection' ? normalizeId(data.collection_id) : '';
				const needsSets = (data.elementSets || []).some(ref => !setsByIdRef.current[normalizeId(ref)]);
				if (cid && needsSets) setCollections.add(cid);
			} catch {
				unreadable.push(id);
			}
		}
		for (const cid of setCollections) {
			try {
				const { data } = await api.get(API_ELEMENT_SETS_URL, { params: { collectionId: cid } });
				if (Array.isArray(data)) sets.push(...data);
			} catch { /* the card still renders its own elements */ }
		}
		if (got.length || sets.length) addCards(got, sets);
		return unreadable;
	}, [addCards]);

	// Record what the on-demand refetch concluded: `missed` it could not get,
	// `found` it now holds (a card that came back is no longer missed).
	const noteMissed = useCallback((missed, found) => {
		setMissedCardIds(prev => {
			const next = new Set(prev);
			missed.forEach(id => next.add(id));
			found.forEach(id => next.delete(id));
			if (next.size === prev.size && [...next].every(id => prev.has(id))) return prev;
			return next.size ? next : NO_IDS;
		});
	}, []);

	/**
	 * Has the on-demand refetch looked for this card and come back without
	 * it? False while it has not asked yet (or is still asking) — so "the
	 * cache does not hold card X" can wait for a verdict instead of calling a
	 * card missing that a by-id read is about to bring (a borrowed card, a
	 * global collection's).
	 */
	const isCardMissed = useCallback(id => missedCardIds.has(normalizeId(id)), [missedCardIds]);

	const noteMissingCards = useCallback((rawIds) => {
		if (!loadedForRef.current) return; // not loaded yet: a miss is not a verdict
		const now = Date.now();
		let queued = false;
		(Array.isArray(rawIds) ? rawIds : [rawIds]).forEach(raw => {
			const id = normalizeId(raw);
			if (!id || cardsByIdRef.current[id]) return;
			const rememberedAt = missRememberedRef.current.get(id);
			if (rememberedAt && now - rememberedAt < MISS_REMEMBER_MS) return;
			if (!missQueueRef.current.has(id)) {
				missQueueRef.current.add(id);
				queued = true;
			}
		});
		if (!queued || missTimerRef.current) return;
		const wait = Math.max(MISS_DEBOUNCE_MS, MISS_MIN_INTERVAL_MS - (now - missLastAtRef.current));
		missTimerRef.current = setTimeout(async () => {
			missTimerRef.current = null;
			const ids = [...missQueueRef.current];
			missQueueRef.current.clear();
			missLastAtRef.current = Date.now();
			let fetched = null;
			try {
				fetched = await load({ force: true });
			} catch {
				fetched = null;
			}
			if (!Array.isArray(fetched)) {
				// The fetch failed; the next miss retries. Until then these
				// ids have an answer — not found — rather than a wait.
				noteMissed(ids, []);
				return;
			}
			const have = { ...indexCardsById(fetched), ...cardsByIdRef.current };
			const stillMissing = ids.filter(id => !have[id]);
			const unreadable = stillMissing.length ? await fetchSingly(stillMissing, have) : [];
			const at = Date.now();
			unreadable.forEach(id => missRememberedRef.current.set(id, at));
			noteMissed(unreadable, ids.filter(id => !unreadable.includes(id)));
		}, wait);
	}, [load, fetchSingly, noteMissed]);

	// Nothing fires after the provider is gone.
	useEffect(() => () => {
		if (missTimerRef.current) clearTimeout(missTimerRef.current);
	}, []);
	const elementSetsById = useMemo(() => indexElementSetsById(elementSets), [elementSets]);
	setsByIdRef.current = elementSetsById;
	const getElementsForCard = useMemo(() => createElementResolver(cardsById, elementSetsById), [cardsById, elementSetsById]);
	const cardNotesEnabled = useMemo(() => createNotesResolver(cardsById), [cardsById]);
	const cardPrimaryDate = useMemo(() => createPrimaryDateResolver(cardsById, elementSetsById), [cardsById, elementSetsById]);

	const value = useMemo(() => ({
		cards,
		cardsById,
		elementSets,
		elementSetsById,
		getElementsForCard,
		cardNotesEnabled,
		cardPrimaryDate,
		loading: fetching || !settled,
		loaded,
		error,
		version,
		ensureLoaded: load,
		refresh,
		refreshElementSets: refresh,
		noteMissingCards,
		isCardMissed,
		addCards,
	}), [cards, cardsById, elementSets, elementSetsById, getElementsForCard, cardNotesEnabled, cardPrimaryDate, fetching, settled, loaded, error, version, load, refresh, noteMissingCards, isCardMissed, addCards]);

	return (
		<CardDefinitionsContext.Provider value={value}>
			{children}
		</CardDefinitionsContext.Provider>
	);
}

export function useCardDefinitions() {
	const ctx = useOptionalCardDefinitions();
	if (!ctx) {
		throw new Error('useCardDefinitions must be used within a CardDefinitionsProvider');
	}
	return ctx;
}

/**
 * Same cache, but returns null instead of throwing when there is no provider —
 * for shared inputs that must still render standalone (InputCardLookup is
 * mounted outside the provider in tests). Callers degrade gracefully rather
 * than requiring the context.
 *
 * Every consumer mount calls ensureLoaded: a no-op while the cache is fresh
 * for the current session, a (re)fetch after a failure or a login.
 */
export function useOptionalCardDefinitions() {
	const ctx = useContext(CardDefinitionsContext);
	const ensureLoaded = ctx?.ensureLoaded;
	useEffect(() => {
		if (ensureLoaded) ensureLoaded();
	}, [ensureLoaded]);
	return ctx;
}

/**
 * A subtree that must never touch the card cache: every consumer inside it
 * reads NO provider, so `useOptionalCardDefinitions` answers null and fires no
 * fetch, and `useCardDefinitions` throws rather than quietly calling home.
 *
 * For the public pages a visitor with no account reaches (the intake form):
 * the cache's `GET /card-definitions` is an authenticated read, and a 401 on a
 * public page is at best a wasted request and at worst — on a path the api
 * client does not list as public — a hard redirect to the login page. This
 * makes the boundary structural rather than a matter of which inputs a page
 * happens to render.
 */
export function WithoutCardDefinitions({ children }) {
	return (
		<CardDefinitionsContext.Provider value={null}>
			{children}
		</CardDefinitionsContext.Provider>
	);
}
